<template>
    <div class="flex flex-col gap-1.5" :data-flashcard-uuid="card.uuid">
        <div class="group relative">
            <FlashcardCard :card="card" :is-flipped="isFlipped" @flip="toggleFlip" />
            <div class="absolute right-2 top-2 z-10 flex items-center gap-0.5">
                <div
                    v-if="!sharedColor"
                    class="flex gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                    @click.stop
                >
                    <PillIconButton
                        v-tooltip.top="localizations[lang].flashcard.edit"
                        icon="baseline-edit-24px"
                        variant="muted"
                        :icon-size="14"
                        :aria-label="localizations[lang].flashcard.edit"
                        @click="$emit('edit', card)"
                    />
                    <PillIconButton
                        v-tooltip.top="localizations[lang].flashcard.delete"
                        icon="baseline-delete-24px"
                        variant="danger"
                        :icon-size="14"
                        :aria-label="localizations[lang].flashcard.delete"
                        @click="$emit('delete', card)"
                    />
                </div>
                <template v-if="sharedColor">
                    <span
                        v-tooltip.top="sharedColor.author && sharedColor.author.email"
                        role="img"
                        :aria-label="sharedLabel"
                        class="mr-2 flex items-center justify-center text-theme-toolbar-icon opacity-70"
                        @click.stop
                    >
                        <svg-icon icon="shared-group-24px" width="25px" height="25px" />
                    </span>
                </template>
            </div>
        </div>
        <button
            v-if="hasSource"
            type="button"
            class="self-center rounded-md px-2.5 py-1 text-xs font-medium text-theme-accent transition-colors hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
            @click.stop="navigateToSource"
        >
            {{ sourceLabel }}
        </button>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import FlashcardCard from './FlashcardCard.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import flashcardSourceMixin from '../../js/mixins/flashcardSourceMixin';

export default {
    name: 'FlashcardListItem',
    components: { FlashcardCard, PillIconButton },
    mixins: [flashcardSourceMixin],
    props: {
        card: { type: Object, required: true },
    },
    data() {
        return {
            isFlipped: false,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang', 'sharedColors']),
        ...mapGetters(['isEpub', 'isPdf', 'onlyMobileVisible']),
        /**
         * Shared color matching this card's slug, when the card belongs to a shared set.
         * @returns {Object|undefined} The shared color (with author info) or undefined
         */
        sharedColor() {
            return this.sharedColors.find(color => color.slug === this.card.colorSlug);
        },
        /**
         * Accessible label for the non-interactive shared indicator.
         * @returns {string} Localized "Shared by: {email}" text, or just "Shared by" when no email
         */
        sharedLabel() {
            const sharedByText = this.localizations[this.lang].sharing?.sharedBy || 'Shared by';
            const authorEmail = this.sharedColor?.author?.email;
            return authorEmail ? `${sharedByText}: ${authorEmail}` : sharedByText;
        },
        /**
         * @returns {Object} The flashcard the source-navigation mixin operates on
         */
        sourceCard() {
            return this.card;
        },
    },
    methods: {
        ...mapActions(['goToPage', 'goToLocation', 'toggleFlashcardsPanel']),
        toggleFlip() {
            this.isFlipped = !this.isFlipped;
        },
    },
};
</script>
